import React, { useCallback, useEffect, useRef, useState } from "react";
import { Badge, Button, Card, EmptyState, Icon, IconButton, PageHeader, SelectField } from "@nelurio/ui";
import { useI18n } from "@nelurio/i18n";
import { api, apiStream, isDemoMode } from "../api/client";
import { useCourses } from "../hooks/useCourses";
import { KhozeAvatar, KhozeHeader } from "../components/Khoze";
import { Link } from "react-router";
import { askKhoze, clearKhozeJob, currentKhozeJob, subscribeKhoze } from "../lib/khozeEngine";
import { courseOptions } from "../lib/assistantCourses";
import { mergeThreadMessages } from "../lib/assistantThread";
import { NotebookView } from "../components/NotebookView";

interface SourceRef { title: string; resourceId?: string; pageNumber?: number; url?: string; }
/** Downscale an attached image for the model: max 1024px, jpeg q0.82 — keeps
 *  the JSON payload inside the 2MB decode limit while staying readable. */
async function compactImage(file: File): Promise<string> {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, 1024 / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext("2d")?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.82);
  } catch {
    return "";
  }
}

interface ChatMessage { id: string; role: "user" | "assistant"; text: string; sources?: Array<string | SourceRef>; khoze?: boolean; saved?: string; images?: string[]; streaming?: boolean; }
interface ThreadSummary { id: string; title: string; courseId: string | null; courseTitle: string | null; courseCategory: string | null; lastMessagePreview: string; lastRole: string; createdAt: string; updatedAt: string; }
interface ThreadMessage { id: string; role: string; content: string; citations: SourceRef[] | null; createdAt: string; }

interface ThreadGroup { key: string; label: string | null; items: ThreadSummary[]; }

/** General chats (no course) first, then one group per course sorted by name;
 *  threads keep the API's newest-activity order inside each group. */
function groupThreads(threads: ThreadSummary[]): ThreadGroup[] {
  const general = threads.filter((thread) => !thread.courseId);
  const byCourse = new Map<string, ThreadSummary[]>();
  for (const thread of threads) {
    if (!thread.courseId) continue;
    const items = byCourse.get(thread.courseId) || [];
    items.push(thread);
    byCourse.set(thread.courseId, items);
  }
  const courseGroups: ThreadGroup[] = Array.from(byCourse.entries()).map(([key, items]) => ({
    key,
    label: items[0].courseCategory || items[0].courseTitle || key,
    items,
  })).sort((a, b) => (a.label || "").localeCompare(b.label || ""));
  const groups: ThreadGroup[] = [];
  if (general.length) groups.push({ key: "general", label: null, items: general });
  return groups.concat(courseGroups);
}

/** "3 h ago"-style relative timestamp for the history meta row. */
function relativeTime(value: string, locale: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  const minutes = Math.round((date.getTime() - Date.now()) / 60000);
  if (Math.abs(minutes) < 60) return formatter.format(minutes, "minute");
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return formatter.format(hours, "hour");
  return formatter.format(Math.round(hours / 24), "day");
}

export function AssistantPage() {
  const { t, locale } = useI18n();
  const { courses } = useCourses();
  // Mode: the free-form Khoze chat, or the NotebookLM-style notebook grounded
  // in the course library. Notebook state lives in NotebookView.
  const [mode, setMode] = useState<"khoze" | "notebook">("khoze");
  // Empty courseId = a general chat (Khoze without course grounding).
  const [courseId, setCourseId] = useState("");
  useEffect(() => { if (courses.length && courseId && !courses.some((course) => course.id === courseId)) setCourseId(""); }, [courses, courseId]);
  const [allowWeb, setAllowWeb] = useState(false);
  const [text, setText] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [threadId, setThreadId] = useState("");
  const threadIdRef = useRef("");
  const streamBubbleRef = useRef("");
  useEffect(() => { threadIdRef.current = threadId; }, [threadId]);
  const [failedMessage, setFailedMessage] = useState<string | null>(null);
  // Active thread lives in SESSION storage: switching tabs keeps the chat, but
  // a fully closed app starts a fresh conversation by default (owner rule).
  const ACTIVE_KEY = "nelurio.ai.active.session";
  const DRAFT_KEY = "nelurio.ai.draft";
  const [attachments, setAttachments] = useState<Array<{ id: string; name: string; kind: "image" | "text" | "file"; previewUrl?: string; textContent?: string; file: File }>>([]);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const addAttachments = async (files: FileList | null) => {
    if (!files?.length) return;
    for (const file of Array.from(files).slice(0, 4)) {
      const id = crypto.randomUUID();
      if (file.type.startsWith("image/")) {
        // data URL (not createObjectURL): the bubble keeps rendering the preview
        // forever, and no blob memory needs revoking on send/remove.
        const dataUrl = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result || ""));
          reader.onerror = () => resolve("");
          reader.readAsDataURL(file);
        });
        setAttachments((current) => [...current, { id, name: file.name, kind: "image", previewUrl: dataUrl, file }]);
      } else if (/\.(txt|md|csv)$/i.test(file.name) && file.size <= 200_000) {
        const textContent = await file.text().catch(() => "");
        setAttachments((current) => [...current, { id, name: file.name, kind: "text", textContent: textContent.slice(0, 12000), file }]);
      } else {
        // PDF & others: land in the course resources (server extraction pipeline).
        setAttachments((current) => [...current, { id, name: file.name, kind: "file", file }]);
      }
    }
  };

  const uploadAttachmentToCourse = async (file: File) => {
    if (!courseId || isDemoMode()) return;
    const form = new FormData();
    form.append("file", file);
    form.append("courseId", courseId);
    form.append("title", file.name);
    await api("/resources", { method: "POST", body: form });
  };

  // Desktop keyboards get Enter-to-send; touch devices keep the send button.
  const IS_COARSE_POINTER = typeof window !== "undefined" && Boolean(window.matchMedia?.("(pointer: coarse)").matches);
  const restoreRef = useRef(false);

  const persistActive = (id: string) => {
    try { window.sessionStorage.setItem(ACTIVE_KEY, JSON.stringify({ threadId: id, courseId })); } catch { /* private mode */ }
  };
  const clearActive = () => { try { window.sessionStorage.removeItem(ACTIVE_KEY); window.localStorage.removeItem(DRAFT_KEY); } catch { /* private mode */ } };

  // Seed the intro only into an EMPTY transcript — `t` changes identity on locale
  // switch, and re-seeding here would wipe the visible conversation.
  useEffect(() => { setMessages((cur) => (cur.length ? cur : [{ id: "intro", role: "assistant", text: t("khoze.intro"), khoze: true }])); }, [t]);

  // Restore the conversation the user was in when they last left this page,
  // so tab switches and navigation never reset an open chat.
  useEffect(() => {
    if (restoreRef.current || isDemoMode()) return;
    restoreRef.current = true;
    let saved: { threadId?: string } | null = null;
    try {
      saved = JSON.parse(window.sessionStorage.getItem(ACTIVE_KEY) || "null");
      const draft = window.localStorage.getItem(DRAFT_KEY);
      if (draft) setText(draft);
    } catch { /* corrupted storage */ }
    const threadId = saved?.threadId;
    if (!threadId) return;
    (async () => {
      try {
        const data = await api<{ thread: { courseId: string | null }; messages: ThreadMessage[] }>(`/ai/threads/${threadId}/messages`);
        setThreadId(threadId);
        if (data.thread.courseId) setCourseId(data.thread.courseId);
        setMessages([
          { id: "intro", role: "assistant", text: t("khoze.intro"), khoze: true },
          ...data.messages.map((message): ChatMessage => ({
            id: message.id,
            role: message.role === "user" ? "user" : "assistant",
            text: message.content,
            sources: Array.isArray(message.citations) ? message.citations : [],
          })),
        ]);
      } catch { /* thread deleted or offline — fresh chat is fine */ }
      // Restore settled (success or not): NOW it is safe to consume a finished
      // engine answer or resume a live one — earlier and the restore could wipe it.
      consumeEngineJob();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live streaming bubble. All updates run inside functional setMessages so
  // restore/consume races resolve against the LATEST list, not a stale snapshot.
  const upsertStreamBubble = (full: string) => {
    setMessages((items) => {
      const id = streamBubbleRef.current;
      if (id && items.some((message) => message.id === id)) {
        return items.map((message) => (message.id === id ? { ...message, text: full, streaming: true } : message));
      }
      if (items.some((message) => message.role === "assistant" && message.text === full)) {
        streamBubbleRef.current = "";
        return items;
      }
      const newId = id || crypto.randomUUID();
      streamBubbleRef.current = newId;
      return [...items, { id: newId, role: "assistant", text: full, streaming: true }];
    });
  };
  const finalizeStreamBubble = (text: string, sources: Array<string | SourceRef>) => {
    const id = streamBubbleRef.current;
    streamBubbleRef.current = "";
    setMessages((items) => {
      if (id && items.some((message) => message.id === id)) {
        return items.map((message) => (message.id === id ? { ...message, text, sources, streaming: false } : message));
      }
      if (items.some((message) => message.role === "assistant" && message.text === text)) return items;
      return [...items, { id: crypto.randomUUID(), role: "assistant", text, sources }];
    });
  };

  // Background engine:
  // Order healer: the server owns the conversation order. Optimistic bubbles and
  // engine/restore races can transiently render an answer ABOVE its question —
  // after an answer settles, merge the local list with the server's (server
  // order wins; a just-sent optimistic bubble survives at the tail).
  const reconcileWithServer = (id: string) => {
    void api<{ thread: { courseId: string | null }; messages: ThreadMessage[] }>(`/ai/threads/${id}/messages`)
      .then((data) => {
        if (threadIdRef.current !== id || streamBubbleRef.current) return;
        setMessages((items) => {
          const [intro, ...local] = items;
          if (!intro || intro.id !== "intro") return mergeThreadMessages(items, data.messages) as ChatMessage[];
          return [intro, ...mergeThreadMessages(local, data.messages)] as ChatMessage[];
        });
      })
      .catch(() => { /* offline — keep the local list */ });
  };

  // Background engine: covers mounted-live, return-mid-answer and
  // return-after-done — the subscription is the SINGLE reply appender.
  useEffect(() => subscribeKhoze((current) => {
    if (!current || current.threadId !== threadIdRef.current) return;
    if (current.status === "thinking") {
      setLoading(true);
      if (current.partial) upsertStreamBubble(current.partial);
    }
    if (current.status === "done" && current.answer) {
      setLoading(false);
      finalizeStreamBubble(current.answer.text, current.answer.sources);
      clearKhozeJob();
      reconcileWithServer(current.threadId);
    }
    if (current.status === "failed") {
      setLoading(false);
      setFailedMessage(current.question);
      clearKhozeJob();
    }
  }), []);

  // Returning to the tab: NEVER drop a finished answer. The restore effect may
  // race the engine's persistence, so consumption happens AFTER restore settles
  // (consumeEngineJob) — mid-answer returns keep the subscription updating live.
  const consumeEngineJob = () => {
    const current = currentKhozeJob();
    if (!current || current.threadId !== threadIdRef.current) return;
    if (current.status === "done" && current.answer) {
      setLoading(false);
      finalizeStreamBubble(current.answer.text, current.answer.sources);
      clearKhozeJob();
      reconcileWithServer(current.threadId);
    } else if (current.status === "thinking") {
      setLoading(true);
      if (current.partial) upsertStreamBubble(current.partial);
    } else if (current.status === "failed") {
      setLoading(false);
      setFailedMessage(current.question);
      clearKhozeJob();
    }
  };

  // History drawer state.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [threads, setThreads] = useState<ThreadSummary[]>([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [threadsError, setThreadsError] = useState(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [busyThreadId, setBusyThreadId] = useState<string | null>(null);
  const [drawerNotice, setDrawerNotice] = useState("");
  const renameBusy = useRef(false);
  const renameCancelled = useRef(false);

  // The send call. appendUser=false on retry (the failed bubble is still shown).
  const runSend = async (messageText: string, appendUser: boolean, visionImages: string[] = []) => {
    if (appendUser) {
      setMessages((items) => [...items, { id: crypto.randomUUID(), role: "user", text: messageText }]);
    }
    setLoading(true);
    try {
      if (isDemoMode()) {
        await new Promise((resolve) => setTimeout(resolve, 600));
        setMessages((items) => [...items, { id: crypto.randomUUID(), role: "assistant", text: t("assistant.demoReply"), sources: [] }]);
      } else {
        if (isDemoMode()) { setMessages((items) => [...items, { id: crypto.randomUUID(), role: "assistant", text: t("assistant.demoReply"), sources: [] }]); return; }
        // askKhoze supervises the request at MODULE scope: leaving this tab keeps
        // the generation running; the floating pill reports it in other tabs and
        // the restored thread picks the answer up from the server on return.
        const answer = await askKhoze(threadId || "", messageText, (notify) => apiStream<{ threadId: string; text: string; sources: SourceRef[] }>("/ai/messages/stream", { courseId: courseId || undefined, courseTitle: courses.find((course) => course.id === courseId)?.title || "", threadId: threadId || undefined, message: messageText, locale, allowWebSearch: allowWeb, images: visionImages }, (full) => notify(full)));
        setThreadId(answer.threadId ? answer.threadId : threadId);
        persistActive(answer.threadId || threadId);
        try { window.localStorage.removeItem(DRAFT_KEY); } catch { /* private mode */ }
        // The bubble update + finalize happen through the engine subscription
        // (single appender); nothing to append here.
        setThreadId(answer.threadId);
        persistActive(answer.threadId);
        try { window.localStorage.removeItem(DRAFT_KEY); } catch { /* private mode */ }
        // NO append here: askKhoze emits "done" BEFORE this continuation runs, so
        // the engine subscription appends first and a second append here rendered
        // the answer twice. The subscription is the single (deduped) appender.
      }
      setFailedMessage(null);
    } catch {
      // Never silently swallow: surface an inline error bubble with a retry.
      setFailedMessage(messageText);
    } finally {
      setLoading(false);
    }
  };

  const send = async (event: React.FormEvent) => {
    event.preventDefault();
    if (loading) return;
    let value = text.trim();
    if (!value && !attachments.length) return;

    // Text attachments are READ into the message so Khoze truly sees them.
    for (const attachment of attachments.filter((entry) => entry.kind === "text" && entry.textContent)) {
      value = `${value}

--- ${attachment.name} ---
${attachment.textContent}`;
    }
    const images = attachments.filter((entry) => entry.kind === "image");
    const files = attachments.filter((entry) => entry.kind === "file");
    // Vision: compact data URLs travel WITH the question so Khoze reads the images.
    const visionImages: string[] = [];
    for (const entry of images.slice(0, 3)) {
      const compact = await compactImage(entry.file);
      if (compact) visionImages.push(compact);
    }
    // Await uploads BEFORE the AI call: the answer can then actually use the
    // freshly uploaded resource, and failures surface instead of vanishing.
    const uploadResults = await Promise.allSettled([...images, ...files].map((entry) => uploadAttachmentToCourse(entry.file)));
    const failedNames = uploadResults.flatMap((result, index) => (result.status === "rejected" ? [[...images, ...files][index].name] : []));
    for (const entry of [...images, ...files]) {
      value = `${value}

(${t("assistant.attach")}: ${entry.name})`;
    }
    if (failedNames.length) {
      value += `

⚠️ ${t("assistant.attach")} (upload failed: ${failedNames.join(", ")})`;
    }

    setMessages((items) => [...items, { id: crypto.randomUUID(), role: "user", text: value, images: images.map((image) => image.previewUrl!) } as typeof items[number]]);
    try { window.localStorage.setItem(DRAFT_KEY, ""); } catch { /* private mode */ }
    setText("");
    setAttachments([]);
    // The user bubble was appended above — appendUser must be false or it renders twice.
    void runSend(value, false, visionImages);
  };

  const retryFailed = () => {
    const value = failedMessage;
    if (!value || loading) return;
    setFailedMessage(null);
    void runSend(value, false);
  };

  // New chat: fresh general conversation — the next message creates a new thread.
  const startNewChat = () => {
    streamBubbleRef.current = "";
    setThreadId("");
    setMessages([{ id: "intro", role: "assistant", text: t("khoze.intro"), khoze: true }]);
    setFailedMessage(null);
    setDrawerOpen(false);
    clearActive();
  };

  const loadThreads = useCallback(async () => {
    if (isDemoMode()) {
      setThreads([]);
      return;
    }
    setThreadsLoading(true);
    setThreadsError(false);
    try {
      const data = await api<{ threads: ThreadSummary[]; nextCursor: string | null }>("/ai/threads?limit=100");
      setThreads(data.threads);
    } catch {
      setThreadsError(true);
    } finally {
      setThreadsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (drawerOpen) void loadThreads();
  }, [drawerOpen, loadThreads]);

  useEffect(() => {
    if (!drawerNotice) return;
    const timer = window.setTimeout(() => setDrawerNotice(""), 4000);
    return () => window.clearTimeout(timer);
  }, [drawerNotice]);

  const openThread = async (thread: ThreadSummary) => {
    setThreadsError(false);
    try {
      const data = await api<{ thread: ThreadSummary; messages: ThreadMessage[] }>(`/ai/threads/${thread.id}/messages`);
      streamBubbleRef.current = "";
      setThreadId(thread.id);
      setCourseId(data.thread.courseId || "");
      persistActive(thread.id);
      setMessages([
        { id: "intro", role: "assistant", text: t("khoze.intro"), khoze: true },
        ...data.messages.map((message): ChatMessage => ({
          id: message.id,
          role: message.role === "user" ? "user" : "assistant",
          text: message.content,
          sources: Array.isArray(message.citations) ? message.citations : [],
        })),
      ]);
      setFailedMessage(null);
      // Below 1280 the drawer is a slide-over, so close it after picking a thread.
      if (window.innerWidth < 1280) setDrawerOpen(false);
    } catch {
      setDrawerNotice(t("assistant.error"));
    }
  };

  const startRename = (thread: ThreadSummary) => {
    renameCancelled.current = false;
    setDeleteConfirmId(null);
    setRenamingId(thread.id);
    setRenameDraft(thread.title);
  };

  const cancelRename = () => {
    renameCancelled.current = true;
    setRenamingId(null);
  };

  // Inline rename: Enter saves, Esc cancels, blur saves. Optimistic update
  // with rollback plus a notice when the server rejects it.
  const commitRename = async (thread: ThreadSummary) => {
    if (renameCancelled.current) {
      renameCancelled.current = false;
      return;
    }
    if (renameBusy.current) return;
    renameBusy.current = true;
    setRenamingId(null);
    const title = renameDraft.trim();
    if (!title || title === thread.title || title.length > 120) {
      renameBusy.current = false;
      if (title.length > 120) setDrawerNotice(t("assistant.error"));
      return;
    }
    const previous = threads;
    setThreads((items) => items.map((item) => (item.id === thread.id ? { ...item, title } : item)));
    try {
      await api(`/ai/threads/${thread.id}`, { method: "PATCH", body: JSON.stringify({ title }) });
    } catch {
      setThreads(previous);
      setDrawerNotice(t("assistant.error"));
    } finally {
      renameBusy.current = false;
    }
  };

  const commitDelete = async (thread: ThreadSummary) => {
    setDeleteConfirmId(null);
    setBusyThreadId(thread.id);
    try {
      await api(`/ai/threads/${thread.id}`, { method: "DELETE" });
      setThreads((items) => items.filter((item) => item.id !== thread.id));
      if (threadId === thread.id) {
        setThreadId("");
        setMessages([{ id: "intro", role: "assistant", text: t("khoze.intro"), khoze: true }]);
        setFailedMessage(null);
      }
    } catch {
      setDrawerNotice(t("assistant.error"));
    } finally {
      setBusyThreadId(null);
    }
  };

  // Khoze can file an answer straight into the course: flashcards and quiz
  // items go to their sections, the summary lands as a note.
  const saveToCourse = async (message: ChatMessage) => {
    if (message.saved || !courseId) return;
    try {
      const result = await api<{ flashcards: number; quiz: number }>("/ai/extract", { method: "POST", body: JSON.stringify({ courseId, text: message.text, locale }) });
      setMessages((items) => items.map((item) => (item.id === message.id ? { ...item, saved: t("study.extracted", { cards: result.flashcards, quiz: result.quiz }) } : item)));
    } catch { /* keep the button for a retry */ }
  };

  const groups = groupThreads(threads);

  return (
    <div className="page-stack assistant-page">
      <PageHeader title={t("ai.title")} subtitle={t("ai.generatedNotice")} />
      <div className="assistant-mode" role="tablist" aria-label={t("ai.title")}>
        <button role="tab" aria-selected={mode === "khoze"} className={mode === "khoze" ? "is-active" : ""} onClick={() => setMode("khoze")}>
          <Icon name="message" size={15} />{t("notebook.modeKhoze")}
        </button>
        <button role="tab" aria-selected={mode === "notebook"} className={mode === "notebook" ? "is-active" : ""} onClick={() => setMode("notebook")}>
          <Icon name="book" size={15} />{t("notebook.modeNotebook")}
        </button>
      </div>
      {mode === "notebook" ? (
        <NotebookView />
      ) : (
      <div className={`assistant-shell${drawerOpen ? " has-drawer" : ""}`}>
        <div className="assistant-layout">
          <Card className="assistant-settings">
            <SelectField label={t("course.title")} value={courseId} onChange={(event) => setCourseId(event.target.value)}>
              {courseOptions(courses, t("assistant.general")).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </SelectField>
            <div className="source-mode">
              <strong>{t("ai.sourceMode")}</strong>
              <button className={!allowWeb ? "is-active" : ""} onClick={() => setAllowWeb(false)}>{t("ai.onlyMine")}</button>
              <button className={allowWeb ? "is-active" : ""} onClick={() => setAllowWeb(true)}>{t("ai.mineAndWeb")}</button>
            </div>
            <div className="assistant-privacy-note"><Icon name="shield" /><p>{t("ai.privacyNote")}</p></div>
          </Card>
          <Card className="chat-panel" elevated>
            <div className="chat-panel__top">
              <KhozeHeader name={t("khoze.name")} tagline={t("khoze.tagline")} />
              <div className="chat-panel__actions">
                <label className="chat-course-picker">
                  <span className="chat-course-picker__label">{t("course.title")}</span>
                  <select
                    className="nl-input nl-select"
                    aria-label={t("course.title")}
                    value={courseId}
                    onChange={(event) => setCourseId(event.target.value)}
                  >
                    {courseOptions(courses, t("assistant.general")).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </label>
                <Button variant="secondary" icon="sparkle" onClick={startNewChat}>{t("assistant.newChat")}</Button>
                <Button variant="secondary" icon="clock" onClick={() => setDrawerOpen(true)}>{t("assistant.history")}</Button>
                <div className="chat-source-chips" role="group">
              <button type="button" className={!allowWeb ? "is-active" : ""} onClick={() => setAllowWeb(false)}>{t("ai.onlyMine")}</button>
              <button type="button" className={allowWeb ? "is-active" : ""} onClick={() => setAllowWeb(true)}>{t("ai.mineAndWeb")}</button>
            </div>
          </div>
            </div>
            <div className="chat-messages">
              {messages.map((message, index) => (
                <article className={`chat-message chat-message--${message.role}`} key={message.id}>
                  <div className="chat-message__avatar">{message.role === "assistant" ? <KhozeAvatar size={35} speaking={loading && index === messages.length - 1} /> : "A"}</div>
                  <div className="chat-message__bubble">
                    {!!message.images?.length && <div className="chat-message__images">{message.images.map((src, index) => <img key={index} src={src} alt="" />)}</div>}
                    <p>{message.text}</p>
                    {!!message.sources?.length && <div className="chat-sources">{message.sources.map((source,index) => { const label=typeof source==="string"?source:source.title; return <Badge key={`${label}-${index}`} tone="neutral"><Icon name="file" size={12} />{label}</Badge>; })}</div>}
                    {message.role === "assistant" && message.text && courseId && (
                      <div className="chat-save">
                        {message.saved
                          ? <Badge tone="green">{message.saved}</Badge>
                          : <Button variant="secondary" icon="plus" onClick={() => void saveToCourse(message)}>{t("study.saveToCourse")}</Button>}
                      </div>
                    )}
                  </div>
                </article>
              ))}
              {failedMessage && (
                <article className="chat-message chat-message--assistant">
                  <div className="chat-message__avatar"><KhozeAvatar size={35} /></div>
                  <div className="chat-message__bubble chat-message__bubble--error">
                    <p>{t("assistant.error")}</p>
                    <div className="chat-error-actions"><Button variant="secondary" onClick={retryFailed}>{t("assistant.retry")}</Button></div>
                  </div>
                </article>
              )}
              {loading && <article className="chat-message chat-message--assistant"><div className="chat-message__avatar"><KhozeAvatar size={35} speaking /></div><div className="chat-message__bubble typing"><i /><i /><i /></div></article>}
            </div>
            {attachments.length > 0 && (
              <div className="chat-attachments" role="list">
                {attachments.map((attachment) => (
                  <span className="chat-attachments__chip" role="listitem" key={attachment.id}>
                    {attachment.kind === "image" ? "🖼️" : "📄"} {attachment.name.slice(0, 22)}
                    <button type="button" onClick={() => setAttachments((current) => current.filter((entry) => entry.id !== attachment.id))} aria-label={t("common.delete")}>×</button>
                  </span>
                ))}
              </div>
            )}
            <form className="chat-composer" onSubmit={send}>
              <input ref={fileRef} type="file" multiple accept="image/*,.txt,.md,.csv,.pdf" hidden onChange={(event) => { void addAttachments(event.target.files); event.target.value = ""; }} />
              <button type="button" className="chat-composer__attach" aria-label={t("assistant.attach")} onClick={() => fileRef.current?.click()}><Icon name="plus" size={20} /></button>
              <textarea value={text} onChange={(event) => { setText(event.target.value); try { window.localStorage.setItem(DRAFT_KEY, event.target.value); } catch { /* private mode */ } }} onKeyDown={(event) => {
                // Desktop: Enter sends, Shift+Enter breaks a line. Touch devices
                // keep the send button (virtual keyboards misuse Enter).
                if (event.key === "Enter" && !event.shiftKey && !IS_COARSE_POINTER && (text.trim() || attachments.length) && !loading) {
                  event.preventDefault();
                  event.currentTarget.form?.requestSubmit();
                }
              }} placeholder={t("ai.placeholder")} rows={1} />
              <Button type="submit" icon="send" disabled={(!text.trim() && !attachments.length) || loading} aria-label={t("ai.send")}>{t("ai.send")}</Button>
            </form>
          </Card>
        </div>
        {drawerOpen && <button type="button" className="assistant-drawer__backdrop" aria-label={t("assistant.close")} onClick={() => setDrawerOpen(false)} />}
        {drawerOpen && (
          <aside className="assistant-drawer" aria-label={t("assistant.history")}>
          <header className="assistant-drawer__header">
            <h2>{t("assistant.history")}</h2>
            <IconButton label={t("assistant.close")} icon="x" onClick={() => setDrawerOpen(false)} />
          </header>
          <Button className="assistant-drawer__new" icon="sparkle" onClick={startNewChat}>{t("assistant.newChat")}</Button>
          {drawerNotice && <p className="assistant-drawer__notice" role="alert">{drawerNotice}</p>}
          <div className="assistant-drawer__body">
            {threadsLoading ? (
              <ul className="assistant-threads">
                <li className="assistant-thread-skeleton"><i /><i /></li>
                <li className="assistant-thread-skeleton"><i /><i /></li>
                <li className="assistant-thread-skeleton"><i /><i /></li>
              </ul>
            ) : threadsError ? (
              <EmptyState icon="warning" title={t("assistant.error")} action={<Button variant="secondary" onClick={() => void loadThreads()}>{t("assistant.retry")}</Button>} />
            ) : threads.length === 0 ? (
              <EmptyState icon="message" title={t("assistant.empty")} action={<Button onClick={startNewChat}>{t("assistant.newChat")}</Button>} />
            ) : (
              groups.map((group) => (
                <section className="assistant-thread-group" key={group.key}>
                  <h3 className="assistant-thread-group__title">{group.label ?? t("assistant.general")}</h3>
                  <ul className="assistant-threads">
                    {group.items.map((thread) => (
                      <li key={thread.id} className={`assistant-thread-item${threadId === thread.id ? " is-active" : ""}${busyThreadId === thread.id ? " is-busy" : ""}`}>
                        {renamingId === thread.id ? (
                          <input
                            className="assistant-thread-item__rename"
                            autoFocus
                            value={renameDraft}
                            maxLength={120}
                            onChange={(event) => setRenameDraft(event.target.value)}
                            onBlur={() => void commitRename(thread)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") void commitRename(thread);
                              if (event.key === "Escape") cancelRename();
                            }}
                          />
                        ) : (
                          <>
                            <button type="button" className="assistant-thread-item__main" onClick={() => void openThread(thread)}>
                              <span className="assistant-thread-item__title">{thread.title}</span>
                              <span className="assistant-thread-item__meta">
                                <span>{relativeTime(thread.updatedAt, locale)}</span>
                                {thread.lastMessagePreview && <span className="assistant-thread-item__preview">{thread.lastMessagePreview}</span>}
                              </span>
                            </button>
                            <span className="assistant-thread-item__tools">
                              {deleteConfirmId === thread.id ? (
                                <>
                                  <span className="assistant-thread-item__confirm">{t("assistant.confirmDelete")}</span>
                                  <IconButton label={t("assistant.delete")} icon="trash" onClick={() => void commitDelete(thread)} />
                                  <IconButton label={t("assistant.cancel")} icon="x" onClick={() => setDeleteConfirmId(null)} />
                                </>
                              ) : (
                                <>
                                  <IconButton label={t("assistant.rename")} icon="edit" onClick={() => startRename(thread)} />
                                  <IconButton label={t("assistant.delete")} icon="trash" onClick={() => { setRenamingId(null); setDeleteConfirmId(thread.id); }} />
                                </>
                              )}
                            </span>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                </section>
              ))
            )}
          </div>
          </aside>
        )}
      </div>
      )}
    </div>
  );
}
